import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CoreQuestionsFactoryService,
  FormQuestionsService,
  CountriesService,
  PopUpService,
  AgentsCacheService,
  ProfileService,
  CustomizationsService,
  CoursesService,
  UtilsQuestionsService
} from 'core/services';
import { QuestionGroup, DropdownQuestion, NumberQuestion, Decorator, QuestionArray } from 'core/components/forms';
import { DateTime } from 'luxon';
import { DefaultDataQuestionsService } from './default-data-questions.service';
import { DatetimeQuestion } from 'core/components/forms/question-datetime';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { MarsGroupService } from '../../../services/mars-groups.service';

const MIN_SESSION_MINUTES = 15;
const MIN_TURN_MINUTES = 15;
const DEFAULT_NUMBER_OF_TURNS = 2;
const MAX_SESSION_DURATION = {
  minutes: 300,
  hours: 48,
  days: 7
};
const MINUTES_FACTOR = {
  minutes: 1,
  hours: 60,
  days: 60 * 24
};

@Injectable()
export class MarsDataQuestionsService extends DefaultDataQuestionsService implements FormQuestionsService {
  sessionConfigQuestion: QuestionGroup;
  basicSessionConfigGroup: QuestionGroup;
  advancedSessionConfigGroup: QuestionGroup;
  protected disabledDatesSubject = new BehaviorSubject([]);
  public datetimeFormat: string;
  public flatpickrFormat: string;

  constructor(
    protected factory: CoreQuestionsFactoryService,
    protected countries: CountriesService,
    protected customizationsService: CustomizationsService,
    protected translateService: TranslateService,
    protected agentsCacheService: AgentsCacheService,
    protected marsGroupsService: MarsGroupService,
    public popupService: PopUpService,
    protected profileService: ProfileService,
    protected coursesService: CoursesService,
    protected utilsQuestionsService: UtilsQuestionsService
  ) {
    super(
      factory,
      countries,
      customizationsService,
      translateService,
      profileService,
      agentsCacheService,
      coursesService,
      utilsQuestionsService
    );
    this.flatpickrFormat = this.translateService.instant('calendar.flatpickrDatetimeFormat');
    this.datetimeFormat = this.translateService.instant('calendar.datetimeFormat');
  }

  getFormData(): any {
    const data = { ...this.form.value };
    delete data._sessions;
    return data;
  }

  buildSession(startDate: Date) {
    const { sessionDuration = '1', sessionDurationType } = this.formData;
    const delta = {};
    delta[sessionDurationType] = parseInt(sessionDuration, 10);
    const startSession = this.getAvailableStartDate(startDate);
    const endDate = this.getAvailableEndDate(startSession.plus(delta).minus({ seconds: 1 }).toJSDate(), startSession);
    const session = {
      startDate: startSession.toJSDate(),
      endDate: endDate.toJSDate()
    };
    return session;
  }

  calculateSessions(params) {
    if (this.formData.startDate && this.formData.maxSessions && this.formData.sessionDuration) {
      const { startDate, maxSessions } = this.formData;
      let startSession = startDate;
      const sessions = Array.from(new Array(parseInt(maxSessions, 10)), () => {
        const session = this.buildSession(startSession);
        startSession = DateTime.fromJSDate(session.endDate, {
          zone: this.timezoneSubject.getValue()
        })
          .plus({ seconds: 1 })
          .toJSDate();
        return session;
      });
      this.refreshSessionsList(sessions, params);
    }
  }

  onCreateSession(params) {
    const { sessions = [] } = this.formData;
    const lastSession = sessions.length ? sessions[sessions.length - 1] : null;
    const startSession = lastSession
      ? DateTime.fromJSDate(new Date(lastSession.endDate), {
          zone: this.timezoneSubject.getValue()
        })
          .plus({ seconds: 1 })
          .toJSDate()
      : DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();
    sessions.push(this.buildSession(startSession));
    this.refreshSessionsList(sessions, params);
    if (sessions.length === 1) {
      this.setGroupStartDate(sessions[0].startDate);
    }
  }

  onDeleteSession(index, params) {
    const { group } = params;
    const { sessions } = group;
    sessions.splice(index, 1);
    this.refreshSessionsList(sessions, params);
    if (index === 0) {
      this.setGroupStartDate(sessions.length ? sessions[0].startDate : null);
    }
  }

  refreshSessionsList(sessions: any[], params) {
    const { disabledDates } = this.formData;
    const endDate = sessions.length ? sessions[sessions.length - 1].endDate : null;
    this.setGroupEndDate(endDate);
    this.form.controls['maxSessions'].setValue(sessions.length);
    this.form.controls['sessions'].setValue(sessions);
    this.validateMaxSessions();

    const _sessionsIndex = this.advancedSessionConfigGroup.childQuestions.findIndex((q) => q.key === '_sessions');
    const _sessionsQuestion = this.getSessionsQuestions(
      {
        ...params,
        group: { sessions, disabledDates }
      },
      false
    );
    this.advancedSessionConfigGroup.childQuestions.splice(_sessionsIndex, 1, _sessionsQuestion);
  }

  checkSessionDuration(startDate, endDate) {
    if (startDate && endDate) {
      const { disabledDates = [] } = this.formData;
      const { sessionDuration = 0, sessionDurationType = 'days' } = this.getSessionDuration(
        startDate,
        endDate,
        disabledDates
      );
      return this.checkSessionDurationValues(sessionDuration, sessionDurationType);
    }
  }

  checkSessionDurationValues(sessionDuration, sessionDurationType) {
    const maxValue = MAX_SESSION_DURATION[sessionDurationType];
    if (sessionDuration > maxValue) {
      return {
        sessionDuration: {
          label: 'mars.maxSessionDurationError',
          maxSessionDuration: maxValue,
          sessionDurationType: this.translateService.instant(`scheduling.interval.${sessionDurationType}`).toLowerCase()
        }
      };
    }
    const sessionMinutes = sessionDuration * MINUTES_FACTOR[sessionDurationType];
    return sessionMinutes < MIN_SESSION_MINUTES
      ? {
          sessionDuration: {
            label: 'mars.minSessionDurationError',
            minSessionMinutes: MIN_SESSION_MINUTES,
            sessionMinutes
          }
        }
      : null;
  }

  updateSessionDurationValues(startDate: Date, endDate: Date) {
    if (startDate && endDate) {
      const { disabledDates = [] } = this.formData;
      const { sessionDuration = 0, sessionDurationType = 'days' } = this.getSessionDuration(
        startDate,
        endDate,
        disabledDates
      );
      if (
        this.form.controls['sessionDurationType'].value !== sessionDurationType ||
        this.form.controls['sessionDuration'].value !== sessionDuration
      ) {
        this.form.controls['sessionDuration'].disable();
        this.form.controls['sessionDurationType'].disable();
        setTimeout(() => {
          this.form.controls['sessionDurationType'].reset(sessionDurationType);
          setTimeout(() => {
            this.form.controls['sessionDuration'].reset(sessionDuration);
            setTimeout(() => {
              this.form.controls['sessionDuration'].enable();
              this.form.controls['sessionDurationType'].enable();
              this.validateSessionDuration();
              this.validateMaxTurns();
            });
          });
        });
      }
    }
  }

  getSessionDuration(startDate, endDate, disabledDates): any {
    if (startDate && endDate) {
      const startSession = DateTime.fromJSDate(new Date(startDate), {
        zone: this.timezoneSubject.getValue()
      });
      const endSession = DateTime.fromJSDate(new Date(endDate), {
        zone: this.timezoneSubject.getValue()
      }).plus({ seconds: 1 });
      const diff = endSession.diff(startSession, ['days', 'hours', 'minutes']).toObject();
      const { hours = 0, minutes = 0 } = diff;
      let { days = 0 } = diff;
      if (disabledDates) {
        disabledDates.forEach((disabledDate) => {
          const disabledDateMillis = DateTime.fromJSDate(new Date(disabledDate), {
            zone: this.timezoneSubject.getValue()
          }).toMillis();
          if (startSession.toMillis() <= disabledDateMillis && disabledDateMillis < endSession.toMillis()) {
            days--;
          }
        });
      }
      const sessionDurationType = minutes > 0 ? 'minutes' : hours > 0 ? 'hours' : 'days';
      const sessionDuration = minutes > 0 ? minutes + hours * 60 + days * 1440 : hours > 0 ? hours + days * 24 : days;
      return { sessionDuration, sessionDurationType };
    }
    return {};
  }

  onStartDateChanged(groupStartDate, params) {
    this.minDateSubject.next(groupStartDate);
    this.formData.startDate = groupStartDate;
    this.calculateSessions(params);
  }

  onDisabledDatesChanged(disabledDates, params) {
    this.formData.disabledDates = disabledDates;
    this.disabledDatesSubject.next(disabledDates);
    this.calculateSessions(params);
  }

  onMaxSessionsChanged(maxSessions, params) {
    this.formData.maxSessions = maxSessions;
    this.calculateSessions(params);
  }

  onsessionDurationChanged(sessionDuration, params) {
    this.formData.sessionDuration = sessionDuration;
    this.validateMaxTurns();
    this.calculateSessions(params);
  }

  onSessionDurationTypeChanged(sessionDurationType, params) {
    this.formData.sessionDurationType = sessionDurationType;
    this.validateSessionDuration();
    this.validateMaxTurns();
    this.calculateSessions(params);
  }

  validateSessionDuration() {
    this.form.controls.sessionDuration.markAsDirty();
    this.form.controls.sessionDuration.updateValueAndValidity();
  }

  validateMaxTurns() {
    this.form.controls.maxTurns.markAsDirty();
    this.form.controls.maxTurns.updateValueAndValidity();
  }

  validateMaxSessions() {
    this.form.controls.maxSessions.markAsDirty();
    this.form.controls.maxSessions.updateValueAndValidity();
  }

  getAvailableStartDate(initialStartDate: Date) {
    const { disabledDates } = this.formData;
    let startDate = DateTime.fromJSDate(new Date(initialStartDate), {
      zone: this.timezoneSubject.getValue()
    });
    if (disabledDates) {
      disabledDates.forEach((disabledDate) => {
        const startDateMillis = startDate.toMillis();
        const disabledDateStart = DateTime.fromJSDate(new Date(disabledDate), {
          zone: this.timezoneSubject.getValue()
        }).toMillis();
        const disabledDateEnd = disabledDateStart + 86400000;
        if (disabledDateStart <= startDateMillis && startDateMillis <= disabledDateEnd) {
          startDate = startDate.plus({ days: 1 });
        }
      });
    }
    return startDate;
  }

  getAvailableEndDate(initialEndDate: Date, startDate: DateTime) {
    const { disabledDates } = this.formData;
    const startDateMillis = startDate.toMillis();
    let endDate = DateTime.fromJSDate(new Date(initialEndDate), {
      zone: this.timezoneSubject.getValue()
    });
    if (disabledDates) {
      disabledDates.forEach((disabledDate) => {
        const endDateMillis = endDate.toMillis();
        const disabledDateMillis = DateTime.fromJSDate(new Date(disabledDate), {
          zone: this.timezoneSubject.getValue()
        }).toMillis();
        if (startDateMillis <= disabledDateMillis && disabledDateMillis <= endDateMillis) {
          endDate = endDate.plus({ days: 1 });
        }
      });
    }
    return endDate;
  }

  getSessionDurationMinutes(sessionDuration) {
    const { sessionDurationType } = this.formData;
    return sessionDuration * MINUTES_FACTOR[sessionDurationType];
  }

  getTurnDurationMinutes(maxTurns) {
    const { sessionDuration } = this.formData;
    return maxTurns ? this.getSessionDurationMinutes(sessionDuration) / maxTurns : 0;
  }

  getPlaytimeMinutes(playOnPlaytime) {
    const playtime = this.formData.playtime;
    const [startHour, startMinute] = playtime[0].split(':').map((val) => parseInt(val, 10));
    const [endHour, endMinute] = playtime[1].split(':').map((val) => parseInt(val, 10));
    const start = startHour * 60 + startMinute;
    const end = endHour * 60 + endMinute;
    const duration = end - start;
    return playOnPlaytime === 'true' ? duration : 24 * 60 - duration;
  }

  createQuestions(params: any = {}) {
    const questions = [this.getGroupDataQuestions(params), this.getSessionConfigurationQuestionGroup(params)];
    // const editable = !params.client?.scorm?.enabled;
    // if (this.userRole.scope !== 'partner') {
    //   questions.push(this.getAgentsQuestionGroup(params, editable));
    // }
    this.questions = questions.filter((question) => question != null);
    this.questionsSubject.next(this.questions);
  }

  setGroupStartDate(sessionStartDate) {
    const startDate = DateTime.fromJSDate(new Date(sessionStartDate)).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    this.form.controls['startDate'].setValue(startDate.toJSDate());
  }

  setGroupEndDate(sessionEndDate) {
    const startDate = DateTime.fromJSDate(new Date(sessionEndDate))
      .plus({ days: 3 })
      .set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
    this.form.controls['endDate'].setValue(startDate.toJSDate());
  }

  getSessionConfigurationQuestionGroup(params) {
    const { group = {} } = params;
    const sessions = group['sessions'] || [];
    this.sessionConfigQuestion = new QuestionGroup({
      title: 'mars.sessionsConfiguration',
      isSection: true,
      childQuestions: [
        this.getBasicSessionConfigQuestion(params),
        this.getAdvancedSessionConfigQuestion(params),
        new QuestionArray({
          key: 'sessions',
          value: sessions
        })
      ]
    });
    return this.sessionConfigQuestion;
  }

  getBasicSessionConfigQuestion(params) {
    const { group } = params;
    const sessions = group && group.sessions ? group.sessions : [];
    const lastSession = sessions.length ? sessions[sessions.length - 1] : null;
    const sessionDuration = lastSession
      ? this.getSessionDuration(new Date(lastSession.startDate), new Date(lastSession.endDate), group.disabledDates)
      : null;
    const groupHasAlreadyStarted =
      group && group.startDate ? new Date(group.startDate).getTime() < new Date().getTime() : false;
    this.basicSessionConfigGroup = new QuestionGroup({
      label: 'mars.basicConfig',
      class: 'col-1',
      childQuestions: [
        new QuestionGroup({
          class: 'col-1',
          childQuestions: [
            this.getMaxSessionsQuestion(params, groupHasAlreadyStarted),
            this.getMaxTurnsQuestion(params, groupHasAlreadyStarted),
            this.getSessionDurationQuestion(params, groupHasAlreadyStarted, sessionDuration),
            this.getSessionDurationTypeQuestion(params, groupHasAlreadyStarted, sessionDuration),
            this.getFederationsTypeQuestion(params, groupHasAlreadyStarted),
            this.getNumFederationsExpectedQuestion(params, groupHasAlreadyStarted),
            this.getNumPlayersExpectedQuestion(params, groupHasAlreadyStarted),
            this.getStartDateGroupQuestion(params, groupHasAlreadyStarted),
            this.getEstimatedEndDateQuestion(params, groupHasAlreadyStarted)
          ]
        }),
        new QuestionGroup({
          class: 'col-1',
          childQuestions: [this.getDisabledDatesQuestion(params, groupHasAlreadyStarted)]
        })
      ].filter((question) => question != null)
    });
    return this.basicSessionConfigGroup;
  }

  getAdvancedSessionConfigQuestion(params) {
    const { group } = params;
    const groupHasAlreadyStarted =
      group && group.startDate ? new Date(group.startDate).getTime() < new Date().getTime() : false;
    this.advancedSessionConfigGroup = new QuestionGroup({
      label: 'mars.advancedConfig',
      title: 'mars.sessions',
      isDropdown: true,
      disableGrid: true,
      class: 'col-1 row',
      childQuestions: [
        this.getSessionsTimezoneDecorator(),
        new QuestionGroup({
          class: 'col-1 table-heading',
          disableGrid: true,
          childQuestions: [
            new Decorator({ value: 'mars.sessionNumber', class: 'col s2 table-item' }),
            new Decorator({ value: 'mars.sessionStartDate', class: 'col s4 table-item' }),
            new Decorator({ value: 'mars.sessionEndDate', class: 'col s4 table-item' })
          ]
        }),
        this.getSessionsQuestions(params, groupHasAlreadyStarted),
        new Decorator({
          class: 'col-1',
          buttonType: !groupHasAlreadyStarted ? 'icon' : null,
          iconClass: 'glicon-plus',
          eventHandlers: {
            click: () => this.onCreateSession(params)
          }
        })
      ]
    });
    return this.advancedSessionConfigGroup;
  }

  getSessionsTimezoneDecorator() {
    const question = new QuestionGroup({
      class: 'col-1',
      childQuestions: [
        new Decorator({ value: 'mars.groupTimezone', class: 'col-2' }),
        new Decorator({ value: '', class: 'col-2 font-bold' })
      ]
    });

    this.timezoneSubject.subscribe((timezone) => {
      question.childQuestions.splice(1, 1, new Decorator({ value: timezone, class: 'col-2 font-bold' }));
    });

    return question;
  }

  getStartDateGroupQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    const minDateIfNotStarted = DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();

    const startDate = new DatetimeQuestion({
      key: 'startDate',
      label: 'nouns.startDate',
      class: 'col-2',
      enableTime: true,
      timezone: this.timezoneSubject.asObservable(),
      minDate: groupHasAlreadyStarted ? DateTime.fromJSDate(new Date(group.startDate)).toJSDate() : minDateIfNotStarted,
      disableDates: this.disabledDatesSubject.asObservable(),
      disabled: groupHasAlreadyStarted,
      value: group ? group.startDate : null,
      validators: groupHasAlreadyStarted ? {} : { required: true, invalidDate: true },
      eventHandlers: groupHasAlreadyStarted
        ? {}
        : {
            onChange: (groupStartDate) => this.onStartDateChanged(groupStartDate, params)
          }
    });
    return startDate;
  }

  getEstimatedEndDateQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    const endDate = new DatetimeQuestion({
      key: 'endDate',
      label: 'nouns.endDate',
      class: 'col-2',
      disabled: groupHasAlreadyStarted,
      editable: false,
      timezone: this.timezoneSubject.asObservable(),
      value: group ? group.endDate : null
    });
    return endDate;
  }

  getDisabledDatesQuestion(params: any, groupHasAlreadyStarted) {
    const { group } = params;
    const minDateIfNotStarted = group
      ? DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).plus({ days: 1 }).toJSDate()
      : DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();
    const disabledDates = group ? group.disabledDates : [];
    this.disabledDatesSubject.next(disabledDates);
    const endDate = new DatetimeQuestion({
      key: 'disabledDates',
      label: 'mars.disabledDates',
      class: 'right disable-dates padding-right',
      timezone: this.timezoneSubject.asObservable(),
      minDate: groupHasAlreadyStarted ? DateTime.fromJSDate(new Date(group.startDate)).toJSDate() : minDateIfNotStarted,
      mode: 'multiple',
      inline: true,
      disabled: groupHasAlreadyStarted,
      value: disabledDates,
      eventHandlers: {
        onChange: (selectedDates) => this.onDisabledDatesChanged(selectedDates, params)
      }
    });
    return endDate;
  }

  getMaxSessionsQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    return new NumberQuestion({
      key: 'maxSessions',
      label: 'mars.maxSessions',
      class: 'col-2',
      editable: !groupHasAlreadyStarted,
      validators: {
        minValue: { limit: 1 },
        maxValue: { limit: 50 }
      },
      value: group ? group.sessions.length : 15,
      eventHandlers: {
        onChange: (value) => this.onMaxSessionsChanged(value, params)
      }
    });
  }

  getMaxTurnsQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    return new NumberQuestion({
      key: 'maxTurns',
      label: 'mars.maxTurns',
      class: 'col-2',
      value: group ? group.maxTurns : DEFAULT_NUMBER_OF_TURNS,
      editable: !groupHasAlreadyStarted,
      validators: {
        required: true,
        minValue: { limit: 1 },
        maxValue: { limit: 50 },
        function: (formControl: UntypedFormControl) => {
          const maxTurns = formControl.value;
          if (!maxTurns || maxTurns <= 0 || maxTurns >= 50) {
            return null;
          }
          const turnMinutes = this.getTurnDurationMinutes(maxTurns);
          return turnMinutes < MIN_TURN_MINUTES
            ? {
                sessionDuration: {
                  label: 'mars.turnDurationError',
                  minTurnMinutes: MIN_TURN_MINUTES,
                  turnMinutes
                }
              }
            : null;
        }
      }
    });
  }

  getSessionDurationQuestion(params, groupHasAlreadyStarted, sessionDurationValues) {
    return new NumberQuestion({
      key: 'sessionDuration',
      class: 'col-2',
      label: 'mars.sessionDuration',
      editable: !groupHasAlreadyStarted,
      value: sessionDurationValues ? sessionDurationValues.sessionDuration : 1,
      validators: {
        required: true,
        minValue: { limit: 1 },
        function: (formControl: UntypedFormControl) => {
          const sessionDuration = formControl.value;
          if (!sessionDuration || sessionDuration <= 0) {
            return null;
          }
          const { sessionDurationType } = this.formData;
          return this.checkSessionDurationValues(sessionDuration, sessionDurationType);
        }
      },
      eventHandlers: {
        onChange: (value) => this.onsessionDurationChanged(value, params)
      }
    });
  }

  getSessionDurationTypeQuestion(params, groupHasAlreadyStarted, sessionDurationValues) {
    const sessionDurationOptions = groupHasAlreadyStarted
      ? [
          { key: 'minutes', value: this.translateService.instant('scheduling.interval.minutes') },
          { key: 'hours', value: this.translateService.instant('scheduling.interval.hours') },
          { key: 'days', value: this.translateService.instant('scheduling.interval.days') }
        ]
      : [
          { key: 'hours', value: this.translateService.instant('scheduling.interval.hours') },
          { key: 'days', value: this.translateService.instant('scheduling.interval.days') }
        ];
    return new DropdownQuestion({
      key: 'sessionDurationType',
      class: 'col-2',
      label: '\u00a0',
      value: sessionDurationValues ? sessionDurationValues.sessionDurationType : 'days',
      disabled: groupHasAlreadyStarted,
      options: [
        { key: 'hours', value: this.translateService.instant('scheduling.interval.hours') },
        { key: 'days', value: this.translateService.instant('scheduling.interval.days') }
      ],
      eventHandlers: {
        onChange: (value) => this.onSessionDurationTypeChanged(value, params)
      }
    });
  }

  getFederationsTypeQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;

    return new DropdownQuestion({
      key: 'federationsType',
      class: 'col-2',
      label: 'nouns.enableFederations',
      value: group ? group.federationsType : 'disabled',
      disabled: groupHasAlreadyStarted,
      options: [
        { key: 'disabled', value: this.translateService.instant('mars.disabled') },
        { key: 'automatic', value: this.translateService.instant('mars.automatic') },
        { key: 'manual', value: this.translateService.instant('mars.manual') }
      ]
    });
  }

  getNumFederationsExpectedQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    return new NumberQuestion({
      key: 'numFederationsExpected',
      label: 'mars.numFederationsExpected',
      class: 'col-2',
      editable: !groupHasAlreadyStarted,
      validators: {
        minValue: { limit: 1 }
      },
      value: group ? group.numFederationsExpected : 1
    });
  }

  getNumPlayersExpectedQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;

    return new NumberQuestion({
      key: 'numPlayersExpected',
      label: 'mars.numPlayersExpected',
      class: 'col-2-offset',
      editable: !groupHasAlreadyStarted,
      validators: {
        minValue: { limit: 1 }
      },
      value: group ? group.numPlayersExpected : 1
    });
  }

  getSessionsQuestions(params, groupHasAlreadyStarted) {
    const { group = {} } = params;
    const sessions = group['sessions'] || [];
    return new QuestionGroup({
      key: '_sessions',
      class: 'col-1 mars-sessions-list',
      disableGrid: true,
      childQuestions: sessions.map((session, index) =>
        this.getSessionQuestions(session, params, index, groupHasAlreadyStarted)
      )
    });
  }

  getSessionQuestions(session, params, index, groupHasAlreadyStarted) {
    const sessionQuestionsGroup = new QuestionGroup({
      key: '_session_' + index,
      class: 'col-1',
      disableGrid: true,
      childQuestions: [
        new Decorator({ value: `${index + 1}`, class: 'col s2 font-x-small center-align' }),
        this.getSessionStartDate(session, params, index, groupHasAlreadyStarted),
        this.getSessionEndDate(session, params, index, groupHasAlreadyStarted),
        new Decorator({
          class: 'col s1 center-align',
          isDefaultClass: false,
          buttonType: !groupHasAlreadyStarted ? 'icon' : null,
          iconClass: 'glicon-minus',
          eventHandlers: {
            click: () => this.onDeleteSession(index, params)
          }
        }),
        new Decorator({
          class: 'col s1 padding-none',
          isDefaultClass: false,
          iconClass: 'glicon-debug-step-over',
          buttonType: !this.isSessionAlreadyFinished(session) ? 'icon' : null,
          eventHandlers: {
            click: () => this.onEndSession(params.group.id, params.group.sessions, index)
          },
          hidden: !this.profileService.canExecute('mars.sessions.finish')
        })
      ]
    });
    return sessionQuestionsGroup;
  }

  onEndSession(groupId, sessions, index) {
    const session = sessions[index];
    const endSessionDate = session.endDate;
    const startSessionDate = session.startDate;
    this.marsGroupsService
      .setActiveSession(groupId, index + 1, startSessionDate, endSessionDate)
      .then(() => {
        this.popupService.showSuccess({ title: 'Session started successfully' });
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.error(err);
        this.popupService.showError({
          text: err.serverMessage
        });
      });
  }

  isSessionAlreadyFinished(session) {
    return DateTime.fromISO(session.endDate) < DateTime.utc();
  }

  getSessionStartDate(session, params, index, groupHasAlreadyStarted) {
    const sessionStartDateQuestion = new DatetimeQuestion({
      key: 'startDate',
      tooltipKey: '_none',
      class: 'col s4 font-x-small padding-none',
      timezone: this.timezoneSubject.asObservable(),
      minDate: session.startDate,
      maxDate: session.endDate,
      enableTime: true,
      disabled: groupHasAlreadyStarted,
      value: session.startDate,
      eventHandlers: groupHasAlreadyStarted
        ? {}
        : {
            onChange: (sessionStartDate, sessionStartDateQuestion) => {
              const sessionEndDateQuestion = sessionStartDateQuestion.parent.getChildQuestion('endDate');
              sessionEndDateQuestion.minDate = sessionStartDate;
              session.startDate = sessionStartDate;
              this.updateSessionDurationValues(session.startDate, session.endDate);
              const error = this.checkSessionDuration(session.startDate, session.endDate);
              sessionStartDateQuestion.errors = error ? [error] : null;
              if (index === 0) {
                this.setGroupStartDate(sessionStartDate);
              }
            }
          }
    });
    return sessionStartDateQuestion;
  }

  getSessionEndDate(session, params, index, groupHasAlreadyStarted) {
    const { group } = params;
    const sessions = this.formData && this.formData.sessions ? this.formData.sessions : group.sessions;
    return new DatetimeQuestion({
      key: 'endDate',
      tooltipKey: '_none',
      class: 'col s4 font-x-small padding-none',
      timezone: this.timezoneSubject.asObservable(),
      minDate: session.startDate,
      maxDate: index < sessions.length - 1 ? session.endDate : null,
      enableTime: true,
      disabled: groupHasAlreadyStarted,
      value: session.endDate,
      eventHandlers: groupHasAlreadyStarted
        ? {}
        : {
            onChange: (sessionEndDate, sessionEndDateQuestion) => {
              const sessionStartDateQuestion = sessionEndDateQuestion.parent.getChildQuestion('startDate');
              sessionStartDateQuestion.maxDate = sessionEndDate;
              session.endDate = sessionEndDate;
              this.updateSessionDurationValues(session.startDate, session.endDate);
              const error = this.checkSessionDuration(session.startDate, session.endDate);
              sessionEndDateQuestion.errors = error ? [error] : null;
              if (index === sessions.length - 1) {
                this.setGroupEndDate(sessionEndDate);
              }
            }
          }
    });
  }
}
